import React from 'react';
import { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Container from '@material-ui/core/Container';
import { AuthAPI } from '../../../api';
import { AuthStore } from '../../../store';
import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';
import ConfigPanel, { ConfigPanelRow } from './ConfigPanel';
import * as Validate from '../../../components/Validate';
import Button from '../../../components/Button';
import Grid from '@material-ui/core/Grid';
import PasswordDialog from './PasswordDialog';
import AlertList from './AlertList';

const ProfileForm = ({loading, profile, dispatch, currentUuid, socialLogin, isAdmin=false, setProfile=null}) => {
  const [alerts, setAlerts] = useState([]);
  const [username, setUsername] = useState(profile.username);
  const [usernameWarning, setUsernameWarning] = useState();
  const [email, setEmail] = useState(profile.email);
  const [emailWarning, setEmailWarning] = useState();
  const [password, setPassword] = useState('');
  const [passwordWarning, setPasswordWarning] = useState();
  const [busy, setBusy] = useState();
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const apiUpdateProfile = isAdmin ? AuthAPI.adminUpdateProfile : AuthAPI.updateProfile;
  const passwordLocked = !isAdmin && socialLogin;
  const disabled = loading || busy;

  // if the profile gets updated, update the form.
  useEffect(() => setEmail(profile.email), [profile.email]);
  useEffect(() => setUsername(profile.username), [profile.username]);

  const addAlerts = (newAlerts) => {
    setAlerts(alerts => [...newAlerts, ...alerts]);
  };

  const removeAlert = (alert) => {
    setAlerts(alerts => alerts.filter(a => a !== alert));
  };

  const onSave = async (currentPassword=undefined) => {

    const valid = [
      Validate.validateEmail(email, setEmailWarning),
      Validate.validateUsername(username, setUsernameWarning),
      Validate.validatePassword(password, setPasswordWarning, true),
    ];
    if (!valid.every(v=>v)) {
      addAlerts([{severity: 'error', text: 'There are errors in one or more of your changes. Please check the fields and try again.'}]);
      return;
    }

    // if the password is changing, prompt for old password.
    if (password !== '' && currentPassword === undefined && !isAdmin) {
      setPasswordDialogOpen(true);
      return;
    }

    // compose the arguments to be sent
    const updates = {};
    if (email !== profile.email) {
      updates.email = email;
    }
    if (password !== '') {
      updates.new_password = password;
      updates.password = currentPassword;
    }
    if (username !== profile.username) {
      updates.username = username;
    }

    setAlerts([]);

    // if there is nothing to update, say so and abort
    if (Object.keys(updates).length === 0) {
      addAlerts([{severity: 'info', text: 'No changes to save.'}]);
      return;
    }

    // send update to the API
    setBusy(true);
    updates.uuid = profile.user_uuid;  // required for admin updates, ignored otherwise
    try {
      const response = await apiUpdateProfile(updates);
      addAlerts(response.payload.messages);
      if (profile.user_uuid === currentUuid) {
        // if isAdmin, may or may not be profile of current user.
        dispatch(AuthStore.profile(response.payload.profile));
      }
      if (setProfile) {
        setProfile(response.payload.profile);
      }
      setPassword('');
    } catch (err) {
      if (err.body) {
        const { message } = err.body;
        addAlerts([{severity: 'error', text: message}]);
      } else {
        addAlerts([{severity: 'error', text: 'An error occurred updating your profile.'}]);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Container maxWidth="md">
      <ConfigPanel defaultExpanded={true} title="Login Details">
        <ConfigPanelRow label="Username">
          <FormControl fullWidth={true}>
            <TextField
              type="text"
              autoComplete="username"
              value={username}
              variant="outlined"
              fullWidth={true}
              placeholder="username"
              margin="normal"
              error={usernameWarning !== undefined}
              helperText={usernameWarning}
              onChange={e => setUsername(e.target.value)}
              onBlur={() => Validate.validateUsername(username, setUsernameWarning)}
            />
          </FormControl>
        </ConfigPanelRow>

        <ConfigPanelRow label="Email Address">
          <FormControl fullWidth={true}>
            <TextField
              type="email"
              autoComplete="email"
              value={email}
              variant="outlined"
              placeholder="email address"
              fullWidth={true}
              margin="normal"
              error={emailWarning !== undefined}
              helperText={emailWarning}
              onChange={e => setEmail(e.target.value)}
              onBlur={() => Validate.validateEmail(email, setEmailWarning)}
            />
          </FormControl>
        </ConfigPanelRow>

        <ConfigPanelRow label="Password">
          <FormControl fullWidth={true}>
            <TextField
              type="password"
              autoComplete="new-password"
              value={password}
              variant="outlined"
              placeholder={passwordLocked ? 'Cannot be changed with social login.' : 'new password'}
              fullWidth={true}
              margin="normal"
              error={passwordWarning !== undefined}
              helperText={passwordWarning}
              onChange={e => setPassword(e.target.value)}
              onBlur={() => Validate.validatePassword(password, setPasswordWarning, true)}
              disabled={passwordLocked}
            />
          </FormControl>
        </ConfigPanelRow>

        <Grid xs={12} item container justify="flex-end">
          <Button label="Save" color="primary" onClick={()=>onSave()} disabled={disabled} busy={busy}/>
        </Grid>
      </ConfigPanel>
      <AlertList alerts={alerts} onClose={removeAlert}/>
      <PasswordDialog
        open={passwordDialogOpen}
        onCancel={()=>setPasswordDialogOpen(false)}
        onSubmit={(password)=>{setPasswordDialogOpen(false); onSave(password);}}
      />
    </Container>
  );
};

ProfileForm.propTypes = {
  currentUuid: PropTypes.string.isRequired,
  socialLogin: PropTypes.bool.isRequired,
  profile: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  dispatch: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool,
  setProfile: PropTypes.func,
};

const mapStateToProps = state => {
  return {
    currentUuid: state.auth.profile.user_uuid,
    socialLogin: !!state.auth.socialLogin,
  };
};

export default connect(mapStateToProps)(ProfileForm);
